import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bot } from 'lucide-react';

export function AIUseLimitations() {
  return (
    <div className="legal-page" data-testid="ai-use-limitations-page">
      <div className="legal-container">
        <Link to="/" className="legal-back-link" data-testid="back-link">
          <ArrowLeft size={18} />
          <span>Back</span>
        </Link>

        <div className="legal-header">
          <div className="legal-icon">
            <Bot size={24} />
          </div>
          <h1>AI Use & Limitations</h1>
          <p className="legal-updated">Last updated: January 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Overview</h2>
            <p>
              TradeLife incorporates AI-powered features designed to assist you with business management tasks. 
              This document explains how AI is used within the platform and its limitations.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. What AI Does in TradeLife</h2>
            <p>AI features in TradeLife are designed to assist with:</p>
            <ul>
              <li>Generating suggestions for quotes, estimates, and job descriptions</li>
              <li>Providing business insights based on your data</li>
              <li>Assisting with communication drafting</li>
              <li>Offering scheduling and workflow suggestions</li>
              <li>Supporting financial analysis and reporting</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. What AI Does Not Do</h2>
            <p>
              <strong>AI in TradeLife does not:</strong>
            </p>
            <ul>
              <li>Make decisions on your behalf</li>
              <li>Commit to pricing, timelines, or scope with your clients</li>
              <li>Execute transactions or financial operations automatically</li>
              <li>Guarantee the accuracy of any suggestions or outputs</li>
              <li>Replace your professional judgement or expertise</li>
              <li>Provide legal, financial, or safety advice</li>
              <li>Guarantee business outcomes or results</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. User Responsibility</h2>
            <p>
              You are solely responsible for:
            </p>
            <ul>
              <li><strong>Reviewing all AI-generated content</strong> before use or sharing with clients</li>
              <li><strong>Verifying pricing</strong> and ensuring it meets your business requirements</li>
              <li><strong>Confirming accuracy</strong> of job descriptions, timelines, and scope</li>
              <li><strong>Making final decisions</strong> on all business matters</li>
              <li><strong>Ensuring compliance</strong> with regulations and industry standards</li>
            </ul>
            <p>
              AI suggestions are starting points for your consideration, not final outputs ready for use.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. AI Limitations</h2>
            <p>AI technology has inherent limitations:</p>
            <ul>
              <li><strong>Context:</strong> AI may not fully understand specific circumstances of your business or clients</li>
              <li><strong>Accuracy:</strong> AI may occasionally produce inaccurate, incomplete, or inappropriate suggestions</li>
              <li><strong>Current Information:</strong> AI may not have access to the latest regulations, pricing, or industry changes</li>
              <li><strong>Regional Variations:</strong> AI suggestions may not account for local requirements or practices</li>
              <li><strong>Complex Situations:</strong> AI may not handle unusual or complex scenarios appropriately</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Data and Privacy</h2>
            <p>
              AI features process your data within the platform:
            </p>
            <ul>
              <li>AI uses only the data you have entered into TradeLife</li>
              <li>Your data is not used to train external AI models</li>
              <li>AI interactions are logged for transparency and audit purposes</li>
              <li>You can review AI activity through the audit log features</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Disclaimer</h2>
            <p>
              AI features are provided as assistance tools only. TradeLife makes no warranties regarding 
              the accuracy, completeness, or suitability of AI-generated content for any particular purpose. 
              Any reliance you place on AI suggestions is strictly at your own risk.
            </p>
            <p>
              TradeLife is not liable for any losses, damages, or issues arising from the use of 
              AI-generated content, including but not limited to incorrect pricing, miscommunication with clients, 
              or business decisions based on AI suggestions.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Wellbeing AI Notice</h2>
            <p>
              If TradeLife includes wellbeing-focused AI features, please note:
            </p>
            <ul>
              <li>These features provide general support and information only</li>
              <li>They are <strong>not</strong> a substitute for professional mental health services</li>
              <li>If you are experiencing a crisis, please contact appropriate emergency services or mental health professionals</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Contact Us</h2>
            <p>If you have questions about AI features, contact us at:</p>
            <p className="legal-contact">
              <strong>Email:</strong> support@tradelife.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AIUseLimitations;
